import React, { useEffect, useState } from 'react';
import { productsApi } from '../lib/api';
import { formatCurrency, getCategoryLabel } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
    Plus, 
    Search, 
    Edit2, 
    Trash2, 
    Package,
    Leaf,
    AlertCircle
} from 'lucide-react';

const CATEGORIES = [
    { value: 'sale', label: 'Salé' },
    { value: 'sucre', label: 'Sucré' },
    { value: 'boisson', label: 'Boisson' },
];

export default function CataloguePage() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        unit_price: '',
        unit_cost: '',
        contains_pork: false,
        is_vegan: false,
        is_halal: false,
        category: 'sale',
    });

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            const response = await productsApi.getAll();
            setProducts(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des produits');
        } finally {
            setLoading(false);
        }
    };

    const filteredProducts = products.filter((product) => {
        const matchesSearch = product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = activeCategory === 'all' || product.category === activeCategory;
        return matchesSearch && matchesCategory;
    });

    const openDialog = (product = null) => {
        if (product) {
            setEditingProduct(product);
            setFormData({
                title: product.title,
                description: product.description,
                unit_price: product.unit_price.toString(),
                unit_cost: product.unit_cost.toString(),
                contains_pork: product.contains_pork || false,
                is_vegan: product.is_vegan || false,
                is_halal: product.is_halal || false,
                category: product.category,
            });
        } else {
            setEditingProduct(null);
            setFormData({
                title: '',
                description: '',
                unit_price: '',
                unit_cost: '',
                contains_pork: false,
                is_vegan: false,
                is_halal: false,
                category: 'sale',
            });
        }
        setIsDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const data = {
            ...formData,
            unit_price: parseFloat(formData.unit_price),
            unit_cost: parseFloat(formData.unit_cost),
        };

        try {
            if (editingProduct) {
                await productsApi.update(editingProduct.id, data);
                toast.success('Produit modifié');
            } else {
                await productsApi.create(data);
                toast.success('Produit créé');
            }
            setIsDialogOpen(false);
            loadProducts();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const handleDelete = async (productId) => {
        if (!window.confirm('Supprimer ce produit ?')) return;
        
        try {
            await productsApi.delete(productId);
            toast.success('Produit supprimé');
            loadProducts();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    if (loading) {
        return (
            <div className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="stat-card h-40 skeleton" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 space-y-6" data-testid="catalogue-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-[#f8fafc]">Catalogue</h1>
                    <p className="text-[#94a3b8] mt-1">{products.length} produits</p>
                </div>
                <Button 
                    onClick={() => openDialog()}
                    data-testid="add-product-btn"
                    className="bg-[#d4af37] text-[#020617] hover:bg-[#fcd34d] font-semibold"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Ajouter un produit
                </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#475569]" />
                    <Input
                        data-testid="search-products-input"
                        placeholder="Rechercher un produit..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-[#0f172a] border-[#1e293b] text-[#f8fafc]"
                    />
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setActiveCategory('all')}
                        className={`category-tab ${activeCategory === 'all' ? 'active' : ''}`}
                        data-testid="category-all-btn"
                    >
                        Tous
                    </button>
                    {CATEGORIES.map((cat) => (
                        <button
                            key={cat.value}
                            onClick={() => setActiveCategory(cat.value)}
                            className={`category-tab ${activeCategory === cat.value ? 'active' : ''}`}
                            data-testid={`category-${cat.value}-btn`}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Products Grid */}
            {filteredProducts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredProducts.map((product, index) => (
                        <div
                            key={product.id}
                            className="product-card animate-fadeInUp"
                            style={{ animationDelay: `${index * 0.05}s` }}
                            data-testid={`product-card-${product.id}`}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <h3 className="text-[#f8fafc] font-semibold text-lg">{product.title}</h3>
                                    <span className="text-[#64748b] text-sm">{getCategoryLabel(product.category)}</span>
                                </div>
                                <div className="flex gap-1">
                                    {product.is_halal && (
                                        <span className="bg-emerald-600 w-6 h-6 rounded flex items-center justify-center text-xs text-white font-bold" title="Halal">
                                            H
                                        </span>
                                    )}
                                    {product.is_vegan && (
                                        <span className="bg-green-500 w-6 h-6 rounded flex items-center justify-center text-xs text-white font-bold" title="Vegan">
                                            V
                                        </span>
                                    )}
                                    {product.contains_pork && (
                                        <span className="bg-red-500 w-6 h-6 rounded flex items-center justify-center text-xs text-white font-bold" title="Contient du porc">
                                            P
                                        </span>
                                    )}
                                </div>
                            </div>
                            
                            {product.description && (
                                <p className="text-[#94a3b8] text-sm mb-4 line-clamp-2">{product.description}</p>
                            )}

                            <div className="flex items-center justify-between pt-3 border-t border-[#1e293b]">
                                <div>
                                    <p className="text-[#d4af37] font-bold text-xl mono">{formatCurrency(product.unit_price)}</p>
                                    <p className="text-[#64748b] text-xs">Coût: {formatCurrency(product.unit_cost)}</p>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openDialog(product)}
                                        className="text-[#94a3b8] hover:text-[#f8fafc] hover:bg-[#1e293b]"
                                        data-testid={`edit-product-${product.id}-btn`}
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDelete(product.id)}
                                        className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                                        data-testid={`delete-product-${product.id}-btn`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-16">
                    <Package className="w-16 h-16 text-[#334155] mx-auto mb-4" />
                    <h3 className="text-[#f8fafc] text-xl font-semibold mb-2">Aucun produit</h3>
                    <p className="text-[#64748b] mb-4">
                        {searchQuery ? 'Aucun résultat pour votre recherche' : 'Commencez par ajouter vos produits'}
                    </p>
                    {!searchQuery && (
                        <Button onClick={() => openDialog()} className="bg-[#d4af37] text-[#020617]">
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter un produit
                        </Button>
                    )}
                </div>
            )}

            {/* Product Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="bg-[#0f172a] border-[#1e293b] text-[#f8fafc] max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold">
                            {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                        <div>
                            <Label className="text-[#94a3b8]">Titre *</Label>
                            <Input
                                data-testid="product-title-input"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Nom du produit"
                                className="mt-1 bg-[#020617] border-[#1e293b] text-[#f8fafc]"
                                required
                            />
                        </div>

                        <div>
                            <Label className="text-[#94a3b8]">Description</Label>
                            <Textarea
                                data-testid="product-description-input"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Description du produit..."
                                className="mt-1 bg-[#020617] border-[#1e293b] text-[#f8fafc]"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-[#94a3b8]">Prix de vente (€) *</Label>
                                <Input
                                    data-testid="product-price-input"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.unit_price}
                                    onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                                    placeholder="0.00"
                                    className="mt-1 bg-[#020617] border-[#1e293b] text-[#f8fafc]"
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-[#94a3b8]">Coût de revient (€) *</Label>
                                <Input
                                    data-testid="product-cost-input"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.unit_cost}
                                    onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                                    placeholder="0.00"
                                    className="mt-1 bg-[#020617] border-[#1e293b] text-[#f8fafc]"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-[#94a3b8]">Catégorie *</Label>
                            <Select
                                value={formData.category}
                                onValueChange={(value) => setFormData({ ...formData, category: value })}
                            >
                                <SelectTrigger 
                                    className="mt-1 bg-[#020617] border-[#1e293b] text-[#f8fafc]"
                                    data-testid="product-category-select"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0f172a] border-[#1e293b]">
                                    {CATEGORIES.map((cat) => (
                                        <SelectItem 
                                            key={cat.value} 
                                            value={cat.value}
                                            className="text-[#f8fafc] focus:bg-[#1e293b] focus:text-[#f8fafc]"
                                        >
                                            {cat.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Diet tags */}
                        <div className="space-y-3 pt-2">
                            <Label className="text-[#94a3b8] text-sm">Régime alimentaire</Label>
                            <div className="flex flex-wrap gap-4">
                                <div className="flex items-center gap-3">
                                    <Switch
                                        id="is_halal"
                                        checked={formData.is_halal}
                                        onCheckedChange={(checked) => setFormData({ ...formData, is_halal: checked })}
                                        data-testid="product-halal-switch"
                                    />
                                    <Label htmlFor="is_halal" className="text-[#94a3b8] cursor-pointer flex items-center gap-2">
                                        <span className="bg-emerald-600 px-1.5 py-0.5 rounded text-xs text-white font-bold">H</span>
                                        Halal
                                    </Label>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Switch
                                        id="is_vegan"
                                        checked={formData.is_vegan}
                                        onCheckedChange={(checked) => setFormData({ ...formData, is_vegan: checked })}
                                        data-testid="product-vegan-switch"
                                    />
                                    <Label htmlFor="is_vegan" className="text-[#94a3b8] cursor-pointer flex items-center gap-2">
                                        <Leaf className="w-4 h-4 text-green-400" />
                                        Vegan
                                    </Label>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Switch
                                        id="contains_pork"
                                        checked={formData.contains_pork}
                                        onCheckedChange={(checked) => setFormData({ ...formData, contains_pork: checked })}
                                        data-testid="product-pork-switch"
                                    />
                                    <Label htmlFor="contains_pork" className="text-[#94a3b8] cursor-pointer flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4 text-pink-400" />
                                        Porc
                                    </Label>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsDialogOpen(false)}
                                className="flex-1 border-[#1e293b] text-[#94a3b8] hover:bg-[#1e293b]"
                            >
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                data-testid="save-product-btn"
                                className="flex-1 bg-[#d4af37] text-[#020617] hover:bg-[#fcd34d]"
                            >
                                {editingProduct ? 'Modifier' : 'Créer'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
